import { FC } from 'react';
import { useSnackbar } from 'notistack';
import { Button, CircularProgress } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { getBackendErrorMessage } from '../../../config/utils';
import { confirmAllConformities, ConformitiesQuery } from '../../../services/TasksService';
import { queryKeys } from '../../../react-query/constants';

interface Props {
    taskId: number;
    query: ConformitiesQuery;
}

const ConformitiesActions: FC<Props> = ({ taskId, query }) => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const confirmAllMutation = useMutation(() => confirmAllConformities(taskId, query), {
        onSuccess: () => {
            queryClient.invalidateQueries([queryKeys.conformities]);
            enqueueSnackbar('All conformities confirmed successfully', { variant: 'success' });
        },
        onError: (error: unknown) => {
            const errorMessage =
                error instanceof Error
                    ? getBackendErrorMessage(error as AxiosError)
                    : 'Failed to confirm conformities';
            enqueueSnackbar(
                t(
                    `errorsEn:${
                        typeof errorMessage === 'string' ? errorMessage : 'Failed to confirm conformities'
                    }`,
                ),
                { variant: 'error' },
            );
        },
    });

    return (
        <Button
            variant="contained"
            color="primary"
            onClick={() => {
                if (!confirmAllMutation.isLoading) {
                    confirmAllMutation.mutate();
                }
            }}
            disabled={confirmAllMutation.isLoading}
            endIcon={confirmAllMutation.isLoading ? null : <CheckIcon />}
        >
            {confirmAllMutation.isLoading ? (
                <CircularProgress color="secondary" size="2rem" />
            ) : (
                'Confirm all'
            )}
        </Button>
    );
};

export default ConformitiesActions;
